import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import { get } from 'lodash';
import { ReactNode, useState } from 'react';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { Form, FormControlProps, InputGroup } from '../../../../bootstrap';
import { Hint } from '../Hint';

// There might be a better way to do this, the errors in TS
// can become confusing if you forget a prop like `label`
type Props<T extends FieldValues> = {
  // Only allow string values to be used with this component
  fieldName: FieldPathByValue<T, string | null | undefined> | FieldPathByValue<T, number | null | undefined>;
  control: Control<T>;
  label?: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  hideErrorMessage?: boolean;
  disabled?: boolean;
  placeholder?: string;
  displayValue?: boolean;
  inputClassnames?: string;
} & Pick<FormControlProps, 'autoFocus' | 'style' | 'maxLength'>;

export function ControlledPasswordInput<T extends FieldValues>({
  label,
  required,
  hint,
  control,
  fieldName,
  hideErrorMessage,
  displayValue,
  inputClassnames,
  ...props
}: Props<T>) {
  const {
    field,
    formState: { errors },
  } = useController({ name: fieldName, control });

  const [showPassword, setShowPassword] = useState(false);

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1 ">*</span>}
        </Form.Label>
      )}
      {displayValue ? (
        <div className="u-fs-2">{field.value ?? 'N/A'}</div>
      ) : (
        <>
          <InputGroup>
            <Form.Control
              className={classNames(inputClassnames, {
                'u-border-color-red1': get(errors, fieldName) && !hideErrorMessage,
              })}
              type={showPassword ? 'text' : 'password'}
              {...field}
              {...props}
            />
            <InputGroup.Text>
              <div
                className="d-flex align-items-center"
                onClick={() => setShowPassword(!showPassword)}
                style={{ cursor: 'pointer' }}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                <span className="ms-1">{showPassword ? 'Hide' : 'Show'}</span>
              </div>
            </InputGroup.Text>
            {hint && <Hint>{hint}</Hint>}
            {!hideErrorMessage && (
              <ErrorMessage
                errors={errors}
                name={fieldName as any} // This *should* work without casting but it doesn't
                render={({ message }) => <div className="u-fs-2 u-color-red1 w-100">{message}</div>}
              />
            )}
          </InputGroup>
        </>
      )}
    </>
  );
}
